/*
 * Netsteps Nosto Search integration
 * --------------------------------------------------------------
 * Phase 1: analytics/tracking foundation (low risk, additive)
 *   - recordSearchSubmit on header + main search form submit
 *   - recordSearch (type='serp') on /search results page load
 *   - recordSearchClick (type='autocomplete') on sidebar product clicks only
 *     (Nosto Search Templates SerpElement tracks PLP clicks as category/serp)
 *   - recordSearchAddToCart (helper exposed for future use)
 *
 * Phase 2: Nosto-powered keyword + popular search suggestions
 *   inside the existing search sidebar (additive, does NOT replace
 *   Shopify predictive products/articles/pages yet).
 *
 * Notes:
 *   - Uses the global `nostojs` queue installed by the Nosto Shopify
 *     app embed (already present in this theme via app blocks).
 *   - Account ID is taken from the Nosto embed context, so we do
 *     not need to hardcode it here.
 *   - Safe to remove: simply remove the <script> tag in theme.liquid
 *     and the small CSS block in netsteps-custom.css; nothing else
 *     in the theme depends on this file.
 *
 * Public API (window.netstepsNostoSearch):
 *   recordSearchSubmit(query)
 *   recordSearchClick(type, hit)         // type: 'serp' | 'autocomplete' | 'category'
 *   recordSearchAddToCart(type, hit)
 *   recordSerpView(query)
 */
(function () {
  if (window.netstepsNostoSearchInit) return;
  window.netstepsNostoSearchInit = true;

  var DEBUG =
    typeof window !== "undefined" &&
    /[?&]nostodebug(=|&|$)/.test(window.location.search || "");

  function log() {
    if (!DEBUG || !window.console) return;
    try {
      console.log.apply(console, ["[nosto-search]"].concat([].slice.call(arguments)));
    } catch (e) {}
  }
  function warn() {
    if (!window.console) return;
    try {
      console.warn.apply(console, ["[nosto-search]"].concat([].slice.call(arguments)));
    } catch (e) {}
  }

  // Run cb(api) once nostojs is available. The Nosto embed defines
  // window.nostojs as a queue function; if it isn't loaded yet we
  // poll briefly. Failing silently is preferred to throwing.
  function withNosto(cb) {
    if (typeof window.nostojs === "function") {
      try {
        window.nostojs(cb);
      } catch (e) {
        warn("nostojs threw", e);
      }
      return;
    }
    var attempts = 0;
    var iv = setInterval(function () {
      attempts++;
      if (typeof window.nostojs === "function") {
        clearInterval(iv);
        try {
          window.nostojs(cb);
        } catch (e) {
          warn("nostojs threw", e);
        }
      } else if (attempts > 50) {
        clearInterval(iv);
        warn("nostojs not available after waiting");
      }
    }, 100);
  }

  // ----- Tracking helpers -----

  function recordSearchSubmit(query) {
    if (!query) return;
    log("recordSearchSubmit", query);
    withNosto(function (api) {
      try {
        api.recordSearchSubmit(query);
      } catch (e) {
        warn("recordSearchSubmit failed", e);
      }
    });
  }

  function normalizeSearchHitUrl(url) {
    if (!url) return "";
    if (/^https?:\/\//i.test(url)) return url;
    try {
      return new URL(url, window.location.origin).href;
    } catch (e) {
      return url;
    }
  }

  function normalizeSearchHit(hit) {
    return {
      productId: String(hit.productId),
      url: normalizeSearchHitUrl(hit.url),
      skuId: hit.skuId != null ? String(hit.skuId) : undefined
    };
  }

  function recordSearchClick(type, hit) {
    if (!type || !hit || !hit.productId) return;
    var payload = normalizeSearchHit(hit);
    log("recordSearchClick", type, payload);
    withNosto(function (api) {
      try {
        api.recordSearchClick(type, payload);
      } catch (e) {
        warn("recordSearchClick failed", e);
      }
    });
  }

  function recordSearchAddToCart(type, hit) {
    if (!type || !hit || !hit.productId) return;
    var payload = normalizeSearchHit(hit);
    log("recordSearchAddToCart", type, payload);
    withNosto(function (api) {
      try {
        api.recordSearchAddToCart(type, payload);
      } catch (e) {
        warn("recordSearchAddToCart failed", e);
      }
    });
  }

  // Build a Nosto-shape result from the Shopify-rendered SERP so we
  // can call recordSearch('serp') without making a Nosto search call.
  // Nosto product IDs match Shopify product IDs because Nosto syncs
  // the catalog via the Shopify integration.
  function buildSerpSnapshot(query) {
    var grid = document.getElementById("ShopProductsGrid");
    var cards = grid ? grid.querySelectorAll(".product-card[data-id]") : [];
    var hits = [];
    for (var i = 0; i < cards.length; i++) {
      var pid = cards[i].getAttribute("data-id");
      if (pid) hits.push({ productId: String(pid) });
    }

    var totalEl = document.getElementById("ShopProductCount");
    var total = hits.length;
    if (totalEl) {
      var match = (totalEl.textContent || "").match(/\d[\d.,]*/);
      if (match) {
        var n = parseInt(match[0].replace(/[^\d]/g, ""), 10);
        if (!isNaN(n)) total = n;
      }
    }

    var pageMatch = (window.location.search.match(/[?&]page=(\d+)/) || [])[1];
    var page = pageMatch ? parseInt(pageMatch, 10) : 1;
    var size = hits.length;
    var from = (page - 1) * size;

    return {
      request: {
        query: query,
        products: { size: size, from: from }
      },
      result: {
        products: {
          hits: hits,
          total: total,
          size: size,
          from: from,
          fuzzy: false
        }
      }
    };
  }

  function recordSerpView(query) {
    if (!query) return;
    var snap = buildSerpSnapshot(query);
    if (!snap.result.products.hits.length && !snap.result.products.total) {
      log("recordSerpView: no products on page, skipping");
    }
    log("recordSearch (serp)", snap);
    withNosto(function (api) {
      try {
        api.recordSearch("serp", snap.request, snap.result, { isKeyword: false });
      } catch (e) {
        warn("recordSearch failed", e);
      }
    });
  }

  // ----- Auto-init: form submit tracking -----
  // Marks query as ORGANIC search. Skipped when the submit was
  // initiated programmatically by a Nosto keyword click (see below).
  var skipNextSubmitTrack = false;

  function initFormSubmitTracking() {
    document.addEventListener(
      "submit",
      function (event) {
        var form = event.target;
        if (!form || !form.matches) return;

        var action = form.getAttribute("action") || "";
        var isSearchForm =
          form.id === "SearchSidebarForm" ||
          form.id === "SearchPageForm" ||
          action.indexOf("/search") !== -1;
        if (!isSearchForm) return;

        if (skipNextSubmitTrack) {
          skipNextSubmitTrack = false;
          log("submit: skipping recordSearchSubmit (keyword-driven)");
          return;
        }

        var input = form.querySelector('input[name="q"]');
        var query = input && input.value ? String(input.value).trim() : "";
        if (query) recordSearchSubmit(query);
      },
      true
    );
  }

  // ----- Auto-init: SERP recordSearch -----
  function initSerpTracking() {
    var path = window.location.pathname || "";
    var isSearch = /\/search(\/|$)/.test(path);
    var qParam = new URLSearchParams(window.location.search).get("q");
    if (!isSearch || !qParam) return;

    if (document.readyState === "complete") {
      recordSerpView(qParam);
    } else {
      window.addEventListener("load", function () {
        recordSerpView(qParam);
      });
    }
  }

  // ----- Auto-init: click tracking for search sidebar autocomplete only -----
  // PLP product clicks are tracked by Nosto Search Templates (<SerpElement />).
  function initClickTracking() {
    document.addEventListener(
      "click",
      function (event) {
        var target = event.target;
        if (!target || !target.closest) return;

        var card = target.closest(".product-card[data-id]");
        if (!card) return;

        var inAutocomplete = !!card.closest(
          'search-sidebar, #SearchSidebar, [data-sidebar-id="SearchSidebar"]'
        );
        if (!inAutocomplete) return;

        var hit = {
          productId: String(card.getAttribute("data-id") || ""),
          url: card.getAttribute("data-url") || ""
        };
        if (!hit.productId) return;

        recordSearchClick("autocomplete", hit);
      },
      true
    );
  }

  // ----- Phase 2: Nosto-powered keyword + popular suggestions -----
  // Renders into a NEW container we inject into the search sidebar
  // ABOVE the existing Shopify predictive markup. We do not replace
  // Shopify's predictive products/articles yet (intentional, lower
  // risk). Submitting a keyword goes to /search via the existing
  // form, so the SERP is still Shopify-native for now.

  function escapeAttr(s) {
    return String(s == null ? "" : s)
      .replace(/&/g, "&amp;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;");
  }
  function escapeHtml(s) {
    return escapeAttr(s);
  }

  var debounceTimer = null;
  function debounce(fn, wait) {
    return function () {
      var ctx = this;
      var args = arguments;
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(function () {
        fn.apply(ctx, args);
      }, wait);
    };
  }

  function ensureSuggestionsContainer(sidebar) {
    var content = sidebar.querySelector("[data-sidebar-search-content-results]");
    if (!content) return null;
    var container = content.querySelector("[data-nosto-suggestions]");
    if (!container) {
      container = document.createElement("div");
      container.setAttribute("data-nosto-suggestions", "");
      container.className = "nosto-suggestions";
      content.insertBefore(container, content.firstChild);
    }
    return container;
  }

  function clearSuggestions(sidebar) {
    var c = sidebar.querySelector("[data-nosto-suggestions]");
    if (c) c.innerHTML = "";
  }

  function renderSuggestions(container, response) {
    var search = (response && response.data && response.data.search) || response || {};
    var keywords = (search.keywords && search.keywords.hits) || [];
    var popular = (search.popularSearches && search.popularSearches.hits) || [];

    var html = "";

    if (keywords.length) {
      html +=
        '<div class="nosto-suggestions__section">' +
        '<div class="nosto-suggestions__title">Suggestions</div>' +
        '<ul class="nosto-suggestions__list">';
      for (var i = 0; i < keywords.length; i++) {
        var kw = keywords[i];
        var keyword = kw.keyword || "";
        if (!keyword) continue;
        var label = (kw._highlight && kw._highlight.keyword) || escapeHtml(keyword);
        html +=
          '<li><button type="button" class="nosto-suggestions__keyword" ' +
          'data-nosto-keyword="' + escapeAttr(keyword) + '" ' +
          'data-nosto-redirect="' + escapeAttr(kw._redirect || "") + '">' +
          label +
          "</button></li>";
      }
      html += "</ul></div>";
    }

    if (popular.length) {
      html +=
        '<div class="nosto-suggestions__section">' +
        '<div class="nosto-suggestions__title">Popular searches</div>' +
        '<ul class="nosto-suggestions__list">';
      for (var j = 0; j < popular.length; j++) {
        var p = popular[j];
        if (!p.query) continue;
        html +=
          '<li><button type="button" class="nosto-suggestions__keyword" ' +
          'data-nosto-keyword="' + escapeAttr(p.query) + '">' +
          escapeHtml(p.query) +
          "</button></li>";
      }
      html += "</ul></div>";
    }

    container.innerHTML = html;
  }

  var lastQuery = "";

  function fetchSuggestions(query, sidebar) {
    var container = ensureSuggestionsContainer(sidebar);
    if (!container) return;
    withNosto(function (api) {
      try {
        api
          .search(
            {
              query: query,
              keywords: {
                fields: ["keyword", "_highlight.keyword"],
                size: 5,
                highlight: { preTag: "<strong>", postTag: "</strong>" }
              },
              popularSearches: { size: 5, emptyQueryMatchesAll: true }
            },
            { track: "autocomplete" }
          )
          .then(function (resp) {
            log("autocomplete response", resp);
            renderSuggestions(container, resp);
          })
          .catch(function (err) {
            warn("autocomplete search failed", err);
          });
      } catch (e) {
        warn("autocomplete search threw", e);
      }
    });
  }

  var debouncedFetch = debounce(fetchSuggestions, 200);

  function initSidebarSuggestions() {
    document.addEventListener(
      "input",
      function (event) {
        var input = event.target;
        if (!input || !input.matches) return;
        if (
          !input.matches(
            "[data-sidebar-search-input], [data-search-field-input]"
          )
        )
          return;
        var sidebar = input.closest("search-sidebar, #SearchSidebar");
        if (!sidebar) return;
        var q = (input.value || "").trim();
        if (q.length < 2) {
          clearSuggestions(sidebar);
          lastQuery = "";
          return;
        }
        if (q === lastQuery) return;
        lastQuery = q;
        debouncedFetch(q, sidebar);
      },
      true
    );

    // Click on a Nosto keyword/popular-search suggestion.
    document.addEventListener(
      "click",
      function (event) {
        var btn =
          event.target && event.target.closest
            ? event.target.closest("[data-nosto-keyword]")
            : null;
        if (!btn) return;
        event.preventDefault();
        event.stopPropagation();

        var keyword = btn.getAttribute("data-nosto-keyword") || "";
        var redirect = btn.getAttribute("data-nosto-redirect") || "";

        // Mark this submission as keyword-driven (not organic) BEFORE
        // navigation so Nosto attributes the search correctly.
        withNosto(function (api) {
          try {
            api.recordSearch(
              "autocomplete",
              { query: keyword },
              {
                products: {
                  hits: [],
                  total: 0,
                  size: 0,
                  from: 0,
                  fuzzy: false
                }
              },
              { isKeyword: true }
            );
          } catch (e) {
            warn("recordSearch keyword failed", e);
          }
        });

        if (redirect) {
          window.location.href = redirect;
          return;
        }

        var sidebar = btn.closest("search-sidebar, #SearchSidebar");
        var form = sidebar && sidebar.querySelector("#SearchSidebarForm");
        var input = sidebar && sidebar.querySelector('input[name="q"]');
        if (input) input.value = keyword;

        skipNextSubmitTrack = true;
        if (form && typeof form.requestSubmit === "function") {
          form.requestSubmit();
        } else if (form) {
          form.submit();
        } else {
          window.location.href =
            "/search?q=" + encodeURIComponent(keyword);
        }
      },
      true
    );
  }

  // ----- Public API -----
  window.netstepsNostoSearch = {
    recordSearchSubmit: recordSearchSubmit,
    recordSearchClick: recordSearchClick,
    recordSearchAddToCart: recordSearchAddToCart,
    recordSerpView: recordSerpView
  };

  // ----- Boot -----
  function boot() {
    initFormSubmitTracking();
    initClickTracking();
    initSerpTracking();
    initSidebarSuggestions();
    log("initialized");
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }
})();
